// Diálogo de ação sobre um utilizador: explica o efeito, pede o motivo (obrigatório, vai para a auditoria),
// chama a API e mostra o erro traduzido se o backend recusar.
import { useTranslation } from "@ecliptica/i18n";
import { Alert, Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Label, Textarea, toast } from "@ecliptica/ui";
import { useState, type ReactNode } from "react";

/** O mínimo de uma resposta da API de que o diálogo precisa (serve para Response e para as do cliente tipado). */
export type ApiResult = { ok: boolean; json: () => Promise<unknown> };

export function ActionDialog({
  open,
  onOpenChange,
  title,
  hint,
  children,
  destructive = false,
  askReason = true,
  canSubmit = true,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: ReactNode;
  hint?: ReactNode;
  children?: ReactNode;
  destructive?: boolean;
  askReason?: boolean;
  canSubmit?: boolean;
  /** Recebe o motivo; devolve a resposta do pedido. */
  onConfirm: (reason: string) => Promise<ApiResult>;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    const res = await onConfirm(reason.trim());
    setBusy(false);
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      const key = `users.actions.errors.${body.error ?? ""}`;
      return setError(body.error && t(key) !== key ? t(key) : t("error.generic"));
    }
    toast.success(t("users.actions.done"));
    setReason("");
    onOpenChange(false);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) setError(null);
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {hint && <DialogDescription>{hint}</DialogDescription>}
        </DialogHeader>
        {children}
        {askReason && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="action-reason">{t("users.actions.reason")}</Label>
            <Textarea id="action-reason" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} className="min-h-20" />
          </div>
        )}
        {error && <Alert variant="destructive">{error}</Alert>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("action.cancel")}
          </Button>
          <Button variant={destructive ? "destructive" : "primary"} disabled={busy || !canSubmit || (askReason && reason.trim().length < 3)} onClick={() => void submit()}>
            {t("users.actions.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
